import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { FilterModel } from '@core/models/filter.model';
import { InviteModel } from '@core/models/invite.model';
import { StatusModel } from '@core/models/status.model';
import { TagModel } from '@core/models/tag.model';
import { UserModel } from '@core/models/user.model';
import * as fromRoot from '@core/redux/index';
import * as LayoutAction from '@core/redux/layout/layout.actions';
import { FilterStoreService } from '@core/services/store/filter.store.service';
import { select, Store } from '@ngrx/store';
import { SelectHeaderUserComponent } from '@ux/components/select/select-header-user/select-header-user.component';
import { SelectComponent } from '@ux/components/select/select.component';
import { PopupStatusComponent } from '@ux/components/select-popup/popup-status/popup-status.component';
import { PopupTagComponent } from '@ux/components/select-popup/popup-tag/popup-tag.component';
import { PopupUserComponent } from '@ux/components/select-popup/popup-user/popup-user.component';
import { StatusComponent } from '@ux/components/status/status.component';
import { TagComponent } from '@ux/components/tag/tag.component';
import { UserComponent } from '@ux/components/user/user.component';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-sidebar-filter',
  templateUrl: './sidebar-filter.component.html',
  styleUrls: ['./sidebar-filter.component.less'],
  imports: [
    FormsModule,
    SelectComponent,
    SelectHeaderUserComponent,
    UserComponent,
    PopupUserComponent,
    StatusComponent,
    PopupStatusComponent,
    TagComponent,
    PopupTagComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarFilterComponent {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly filterStoreService = inject(FilterStoreService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() inviteList: InviteModel[];

  users = signal<UserModel[]>([]);
  statuses = signal<StatusModel[]>([]);
  tags = signal<TagModel[]>([]);
  footerView = signal<'default' | 'saveFilter'>('default');
  filterModel = signal<FilterModel>(this.filterStoreService.getNewFilter());

  constructor() {
    // Подписка на активных пользователей
    this.store
      .pipe(select(fromRoot.getInviteActiveEntities), takeUntilDestroyed(this.destroyRef))
      .subscribe((invites: InviteModel[]) => {
        const updatedUsers: UserModel[] = [];
        invites.forEach((item: InviteModel) => {
          updatedUsers.push(item.user);
        });
        this.users.set(updatedUsers);
      });

    // Подписка на статусы
    this.store
      .pipe(select(fromRoot.getStatusEntities), takeUntilDestroyed(this.destroyRef))
      .subscribe((statuses: StatusModel[]) => this.statuses.set(statuses));

    // Подписка на теги
    this.store
      .pipe(select(fromRoot.getTagEntities), takeUntilDestroyed(this.destroyRef))
      .subscribe((tags: TagModel[]) => this.tags.set(tags));

    // Подписка на комбинацию фильтров
    const selectFilter$ = this.store.pipe(select(fromRoot.getFilterSelect));
    const routeFilter$ = this.route.queryParams;

    combineLatest([selectFilter$, routeFilter$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([selectFilter, routeFilter]) => {
        if (selectFilter) {
          this.filterModel.set(selectFilter);
        } else if (routeFilter) {
          this.filterModel.set(this.filterStoreService.getNewFilterByParams(routeFilter));
        } else {
          this.filterModel.set(this.filterStoreService.getNewFilter());
        }
      });
  }

  onChangeResponsible(value: string | string[]): void {
    const currentFilter = this.filterModel();
    currentFilter.responsibleIds.length = 0;
    if (Array.isArray(value)) {
      currentFilter.responsibleIds.push(...value);
    } else if (value) {
      currentFilter.responsibleIds.push(value);
    }
    this.filterModel.set({ ...currentFilter });
  }

  onChangeAuthor(value: string | string[]): void {
    const currentFilter = this.filterModel();
    currentFilter.authorIds.length = 0;
    if (Array.isArray(value)) {
      currentFilter.authorIds.push(...value);
    } else if (value) {
      currentFilter.authorIds.push(value);
    }
    this.filterModel.set({ ...currentFilter });
  }

  onChangeStatus(value: string | string[]): void {
    const currentFilter = this.filterModel();
    currentFilter.statusIds.length = 0;
    if (Array.isArray(value)) {
      currentFilter.statusIds.push(...value);
    } else if (value) {
      currentFilter.statusIds.push(value);
    }
    this.filterModel.set({ ...currentFilter });
  }

  onChangeTag(value: string | string[]): void {
    const currentFilter = this.filterModel();
    currentFilter.tagIds.length = 0;
    if (Array.isArray(value)) {
      currentFilter.tagIds.push(...value);
    } else if (value) {
      currentFilter.tagIds.push(value);
    }
    this.filterModel.set({ ...currentFilter });
  }

  onClose(): void {
    this.filterStoreService.selectFilter(null);
    this.store.dispatch(new LayoutAction.CloseSidebarFilter());
  }

  onSubmit(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.filterStoreService.getFilterByParamsForQueryParams(this.filterModel()),
    });
  }

  onResetFilter(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  onSaveFilter(): void {
    if (this.footerView() === 'default') {
      this.footerView.set('saveFilter');
    } else {
      const currentFilter = this.filterModel();
      if (currentFilter.id) {
        this.filterStoreService.saveFilter(currentFilter);
        this.filterStoreService.selectFilter(null);
      } else {
        this.filterStoreService.createFilter(currentFilter);
      }
      this.footerView.set('default');
    }
  }
}
